import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Check, Calendar } from 'lucide-react';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';

// ══════════════════════════════════════
// ACADEMIC YEARS
// ══════════════════════════════════════
export function AcademicYearsPage() {
  const [years, setYears]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(false);
  const [editing, setEditing]   = useState(null);
  const [confirm, setConfirm]   = useState(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/masters/academic-years');
      setYears(data.data);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); reset({ year_label:'', start_date:'', end_date:'', is_active: false }); setModal(true); };
  const openEdit   = (row) => {
    setEditing(row);
    reset({
      year_label:  row.year_label,
      start_date:  row.start_date?.substring(0,10),
      end_date:    row.end_date?.substring(0,10),
      is_active:   !!row.is_active,
    });
    setModal(true);
  };

  const onSubmit = async (values) => {
    try {
      if (editing) {
        await api.put(`/masters/academic-years/${editing.id}`, values);
        toast.success('Updated');
      } else {
        await api.post('/masters/academic-years', values);
        toast.success('Created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/masters/academic-years/${id}`);
      toast.success('Deleted');
      setConfirm(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Academic Years</h1>
          <p className="page-subtitle">Manage academic year periods</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Add Year</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader"/></div> : (
            <table className="data-table">
              <thead><tr>
                <th>Year</th><th>Start Date</th><th>End Date</th><th>Status</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {years.map(y => (
                  <tr key={y.id}>
                    <td><span style={{ fontWeight: 600, color: 'var(--grey-800)', display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} style={{ color: 'var(--orange-400)' }}/>{y.year_label}</span></td>
                    <td>{y.start_date?.substring(0,10)}</td>
                    <td>{y.end_date?.substring(0,10)}</td>
                    <td>{y.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-grey">Inactive</span>}</td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn-icon" onClick={() => openEdit(y)}><Pencil size={14}/></button>
                        <button className="btn-icon" onClick={() => setConfirm(y.id)}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!years.length && <tr><td colSpan={5} className="empty-state"><Calendar size={32}/><h3>No academic years yet</h3></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} Academic Year</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Year Label <span className="req">*</span></label>
                  <input className={`form-control ${errors.year_label?'error':''}`} placeholder="2025-26"
                    {...register('year_label',{required:'Required'})} />
                  {errors.year_label && <p className="form-error">{errors.year_label.message}</p>}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Start Date <span className="req">*</span></label>
                    <input type="date" className={`form-control ${errors.start_date?'error':''}`}
                      {...register('start_date',{required:'Required'})} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Date <span className="req">*</span></label>
                    <input type="date" className={`form-control ${errors.end_date?'error':''}`}
                      {...register('end_date',{required:'Required'})} />
                  </div>
                </div>
                <label className="toggle">
                  <input type="checkbox" {...register('is_active')} />
                  <span className="toggle-track"><span className="toggle-thumb"/></span>
                  <span style={{fontSize:13}}>Set as Active Year</span>
                </label>
                <p style={{fontSize:11,color:'var(--grey-400)',marginTop:4}}>Activating will deactivate the current active year.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!confirm} title="Delete Academic Year" message="This cannot be undone."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}

// ══════════════════════════════════════
// DISTRICTS
// ══════════════════════════════════════
export function DistrictsPage() {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [search, setSearch]       = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try { const { data } = await api.get('/masters/districts'); setDistricts(data.data); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = districts.filter(d =>
    d.district_name.toLowerCase().includes(search.toLowerCase()) ||
    d.state_name.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setEditing(null); reset({ state_name:'', district_name:'', district_code:'', is_active: true }); setModal(true); };
  const openEdit   = (row) => { setEditing(row); reset({ state_name: row.state_name, district_name: row.district_name, district_code: row.district_code||'', is_active: !!row.is_active }); setModal(true); };

  const onSubmit = async (values) => {
    try {
      if (editing) { await api.put(`/masters/districts/${editing.id}`, values); toast.success('Updated'); }
      else         { await api.post('/masters/districts', values); toast.success('Created'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/masters/districts/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Districts</h1>
          <p className="page-subtitle">Manage districts across India</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Add District</button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-bar" style={{ minWidth: 280 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search district or state…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <span className="badge badge-orange">{districts.length} districts</span>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader"/></div> : (
            <table className="data-table">
              <thead><tr><th>State</th><th>District</th><th>Code</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id}>
                    <td>{d.state_name}</td>
                    <td style={{fontWeight:600}}>{d.district_name}</td>
                    <td><code style={{fontSize:11,background:'var(--grey-100)',padding:'2px 6px',borderRadius:4}}>{d.district_code||'—'}</code></td>
                    <td>{d.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-grey">Inactive</span>}</td>
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn-icon" onClick={() => openEdit(d)}><Pencil size={14}/></button>
                        <button className="btn-icon" onClick={() => setConfirm(d.id)}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={5}><div className="empty-state"><p>No districts found</p></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} District</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">State Name <span className="req">*</span></label>
                  <input className={`form-control ${errors.state_name?'error':''}`} placeholder="Maharashtra"
                    {...register('state_name',{required:'Required'})} />
                </div>
                <div className="form-group">
                  <label className="form-label">District Name <span className="req">*</span></label>
                  <input className={`form-control ${errors.district_name?'error':''}`} placeholder="Pune"
                    {...register('district_name',{required:'Required'})} />
                </div>
                <div className="form-group">
                  <label className="form-label">District Code</label>
                  <input className="form-control" placeholder="Census code (optional)"
                    {...register('district_code')} />
                </div>
                <label className="toggle">
                  <input type="checkbox" {...register('is_active')} />
                  <span className="toggle-track"><span className="toggle-thumb"/></span>
                  <span style={{fontSize:13}}>Active</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog open={!!confirm} title="Delete District" message="All pincodes in this district will also be affected."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}

// ══════════════════════════════════════
// PINCODES
// ══════════════════════════════════════
export function PincodesPage() {
  const [pincodes, setPincodes]   = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirm, setConfirm]     = useState(null);
  const [filterDistrict, setFilterDistrict] = useState('');

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([
        api.get('/masters/pincodes'),
        api.get('/masters/districts?active=true'),
      ]);
      setPincodes(p.data.data);
      setDistricts(d.data.data);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = pincodes.filter(p =>
    !filterDistrict || String(p.district_id) === filterDistrict
  );

  const openCreate = () => { setEditing(null); reset({ district_id:'', pincode:'', area_name:'', is_active:true }); setModal(true); };
  const openEdit   = (row) => { setEditing(row); reset({ district_id: row.district_id, pincode: row.pincode, area_name: row.area_name||'', is_active: !!row.is_active }); setModal(true); };

  const onSubmit = async (values) => {
    try {
      if (editing) { await api.put(`/masters/pincodes/${editing.id}`, values); toast.success('Updated'); }
      else         { await api.post('/masters/pincodes', values); toast.success('Created'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/masters/pincodes/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pincodes</h1>
          <p className="page-subtitle">Manage pincodes linked to districts</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={15}/> Add Pincode</button>
      </div>

      <div className="card">
        <div className="card-header">
          <select className="form-control" style={{width:240}} value={filterDistrict} onChange={e => setFilterDistrict(e.target.value)}>
            <option value="">All Districts</option>
            {districts.map(d => <option key={d.id} value={d.id}>{d.district_name} ({d.state_name})</option>)}
          </select>
          <span className="badge badge-orange">{filtered.length} pincodes</span>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader"/></div> : (
            <table className="data-table">
              <thead><tr><th>Pincode</th><th>Area</th><th>District</th><th>State</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td><code style={{fontFamily:'var(--font-mono,monospace)',fontWeight:700,color:'var(--orange-600)'}}>{p.pincode}</code></td>
                    <td>{p.area_name||'—'}</td>
                    <td>{p.district_name}</td>
                    <td style={{color:'var(--grey-400)',fontSize:12}}>{p.state_name}</td>
                    <td>{p.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-grey">Inactive</span>}</td>
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn-icon" onClick={() => openEdit(p)}><Pencil size={14}/></button>
                        <button className="btn-icon" onClick={() => setConfirm(p.id)}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={6}><div className="empty-state"><p>No pincodes found</p></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Add'} Pincode</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">District <span className="req">*</span></label>
                  <select className={`form-control ${errors.district_id?'error':''}`}
                    {...register('district_id',{required:'Required'})}>
                    <option value="">Select district…</option>
                    {districts.map(d => <option key={d.id} value={d.id}>{d.district_name} — {d.state_name}</option>)}
                  </select>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Pincode <span className="req">*</span></label>
                    <input className={`form-control ${errors.pincode?'error':''}`} placeholder="400001" maxLength={6}
                      {...register('pincode',{required:'Required',minLength:{value:6,message:'6 digits'},maxLength:{value:6,message:'6 digits'}})} />
                    {errors.pincode && <p className="form-error">{errors.pincode.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Area / Locality</label>
                    <input className="form-control" placeholder="Post office name" {...register('area_name')} />
                  </div>
                </div>
                <label className="toggle">
                  <input type="checkbox" {...register('is_active')} />
                  <span className="toggle-track"><span className="toggle-thumb"/></span>
                  <span style={{fontSize:13}}>Active</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14}/> Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ConfirmDialog open={!!confirm} title="Delete Pincode" message="This will remove the pincode and its territory assignments."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
